import { ReactNode } from 'react'

import { Paginator, SkeletonLoader } from '@/components/derived'

import { TableHeader } from './Table.Header'
import { TableRows } from './Table.Rows'
import { Divider, ScrollContainer, TableComponent, TableWrapper } from './Table.styles'

export interface ColumnType<T> {
  key: string
  title?: string | ReactNode
  width?: number
  align?: 'left' | 'center' | 'right'
  render?: (column: ColumnType<T>, item: T, idx: number) => ReactNode
}

export interface TableProps<T> {
  data?: T[]
  columns: ColumnType<T>[]
  totalPages?: number
  isLoading?: boolean
  name?: string
  disableRowStriping?: boolean
  isScrollable?: boolean
  maxHeight?: string
  renderRowTags?: (item: T, index: number, columns: ColumnType<T>[]) => ReactNode

  enableSelection?: boolean
  selectedItems?: Set<any>
  onItemSelect?: (item: T) => void
  selectAllChecked?: boolean
  onSelectAll?: () => void
  getItemId?: (item: T) => any
}

export function Table<T>({
  name,
  columns,
  data,
  totalPages = 1,
  isLoading = false,
  disableRowStriping = false,
  isScrollable = false,
  maxHeight = '400px',
  renderRowTags,
  enableSelection = false,
  selectedItems,
  onItemSelect,
  selectAllChecked = false,
  onSelectAll,
  getItemId,
}: TableProps<T>) {
  if (!isLoading && (!data || data.length < 1)) return null

  return (
    <TableWrapper>
      {isLoading ?
        <SkeletonLoader amount={3} />
      : <>
          <ScrollContainer $isScrollable={isScrollable} $maxHeight={maxHeight}>
            <TableComponent>
              <thead>
                <TableHeader
                  columns={columns}
                  enableSelection={enableSelection}
                  selectAllChecked={selectAllChecked}
                  onSelectAll={onSelectAll}
                />
              </thead>
              <tbody>
                <TableRows
                  data={data}
                  columns={columns}
                  disableRowStriping={disableRowStriping}
                  renderRowTags={renderRowTags}
                  enableSelection={enableSelection}
                  selectedItems={selectedItems}
                  onItemSelect={onItemSelect}
                  getItemId={getItemId}
                />
              </tbody>
            </TableComponent>
            <Divider />
            <Paginator totalPages={totalPages} name={name} />
          </ScrollContainer>
        </>
      }
    </TableWrapper>
  )
}
